<template>
  <v-dialog fullscreen v-model="showDialog">
    <v-card>
      <v-card-title class="secondary white--text rounded-0">
        <span>Bulk Sub-hire Update - (Amend)</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="resetForm(); showDialog = false">
          <v-icon class="white--text">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-3 pb-3">
        <v-form ref="filterForm" v-if="showDialog">
          <v-row align="center">
            <v-col cols="12" sm="6" md="4" lg="2" class="Form-Columns">
              <v-textarea
                outlined
                dense
                rows="1"
                auto-grow
                label="DA No (multi-search)"
                clearable
                hide-details="auto"
                placeholder="e.g. DA123, DA456"
                v-model="filters.da_nos"
                @keyup.enter="searchList"
              />
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="3" class="Form-Columns">
              <v-text-field
                outlined
                dense
                label="Container No (comma separated)"
                clearable
                hide-details="auto"
                placeholder="e.g. ABCD1234567, EFGH7654321"
                v-model="filters.container_nos"
                @keyup.enter="searchList"
              />
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2" class="Form-Columns">
              <DateField
                :inputProps="{
                  dense: true,
                  label: 'Start Date',
                  clearable: true,
                  'hide-details': 'auto',
                }"
                v-model="filters.start_date"
                @change="delete filters.end_date"
              />
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2" class="Form-Columns">
              <DateField
                :inputProps="{
                  dense: true,
                  label: 'End Date',
                  clearable: true,
                  'hide-details': 'auto',
                }"
                :min="filters.start_date"
                v-model="filters.end_date"
              />
            </v-col>

            <v-col cols="12" sm="6" md="3" lg="1" class="Form-Columns d-flex align-center">
              <v-switch
                dense
                label="Sub Hire"
                hide-details="auto"
                class="mt-0"
                v-model="filters.is_sub_hire"
              />
            </v-col>

            <v-col cols="12" sm="6" md="auto" class="Form-Columns d-flex align-center">
              <v-btn depressed color="primary" class="mr-2" @click="searchList">
                Search
              </v-btn>
              <v-btn depressed color="secondary" @click="resetForm">
                Reset
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider class="mt-2" />

      <v-card-text class="py-3">
        <v-row align="center">
          <v-col cols="auto">
            <span class="text-subtitle-1">
              Total Selected:
              <strong>{{ selectedRows.length }}</strong>
            </span>
          </v-col>
          <v-spacer />
          <v-col cols="auto" class="d-flex align-center">
            <span class="mr-2 text-body-2 font-weight-medium">Set Sub-hire</span>
            <v-switch
              dense
              hide-details="auto"
              class="mt-0 mr-4"
              v-model="subHireValue"
              :label="subHireValue ? 'Yes' : 'No'"
              color="success"
            />
            <v-btn
              depressed
              color="primary"
              :disabled="!selectedRows.length"
              @click="confirmDialog = true"
            >
              Update Status
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-text class="pt-2 pb-0">
        <AgGridVue
          style="width: 100%; height: calc(100vh - 260px)"
          class="ag-theme-alpine"
          :columnDefs="headers"
          :context="context"
          @grid-ready="onGridReady"
          rowSelection="multiple"
          @selection-changed="setSelectedRows"
          :defaultColDef="defaultColDef"
          :grid-options="gridOptions"
          :suppressDragLeaveHidesColumns="true"
          :rowData="allRowList"
        />

        <v-col v-if="totalItems > 0" cols="12" class="d-flex justify-end pa-0 pt-1">
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
      </v-card-text>
    </v-card>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="text-h6">Confirm Update</v-card-title>
        <v-card-text>
          Are you sure you want to set Sub-hire to
          <strong>{{ subHireValue ? 'Yes' : 'No' }}</strong>
          for <strong>{{ selectedRows.length }}</strong> selected DA(s)?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn depressed @click="confirmDialog = false">Cancel</v-btn>
          <v-btn depressed color="primary" @click="submitForm">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import Pagination from "@/components/General/Pagination.vue";

export default {
  components: { AgGridVue, DateField, Pagination },

  props: {
    value: { type: Boolean, default: false },
  },

  watch: {
    value(val) {
      if (val) {
        this.getDeliveryAdviceList();
      } else {
        this.resetForm();
      }
    },
    'filters.da_nos'(val) {
      if (!val) this.searchList();
    },
    'filters.container_nos'(val) {
      if (!val) { this.searchList(); return; }
      // Normalize: replace newlines/extra spaces with comma so pasting a list works cleanly
      const normalized = val.replace(/[\r\n]+/g, ',').replace(/\s*,\s*/g, ',').replace(/,+/g, ',').replace(/^,|,$/g, '');
      if (normalized !== val) {
        this.filters.container_nos = normalized;
      }
    },
  },

  computed: {
    showDialog: {
      get() { return this.value; },
      set(val) { this.$emit("input", val); },
    },
    context() { return { parentComponent: this }; },
    offset() { return this.itemsPerPage * (this.pageNo - 1); },
  },

  data() {
    return {
      selectedRows: [],
      allRowList: [],
      filters: { is_sub_hire: false },
      subHireValue: true,
      confirmDialog: false,
      pageNo: 1,
      itemsPerPage: 50,
      totalItems: 0,
      defaultColDef: { resizable: true },
      gridOptions: {
        onGridSizeChanged: () => { this.resizeGrid(); },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
      headers: [
        { headerName: "", checkboxSelection: true, headerCheckboxSelection: true, minWidth: 50, maxWidth: 50 },
        { headerName: "DA No.", field: "da_no", minWidth: 120 },
        { headerName: "Status", field: "status", minWidth: 100 },
        { headerName: "Customer", field: "customer_name", minWidth: 180 },
        { headerName: "VBS Ref No", field: "vbs_ref_no", minWidth: 130 },
        { headerName: "Lane", field: "lane_name", minWidth: 180 },
        { headerName: "Container No(s)", field: "container_nos", minWidth: 160 },
        { headerName: "Movement Date/Time", field: "movement_date_time", minWidth: 160 },
        { headerName: `Rate (${this.$globals.currency})`, field: "total_da_rate", minWidth: 110 },
        { headerName: "BOE", field: "boe", minWidth: 110 },
        { headerName: "Sub Hire", field: "is_sub_hire", minWidth: 90 },
        { headerName: "Transporter", field: "transporter_name", minWidth: 180 },
      ],
    };
  },

  methods: {
    setSelectedRows() {
      this.selectedRows = this.gridApi ? this.gridApi.getSelectedRows() : [];
    },
    resizeGrid() {
      if (this.gridOptions && "api" in this.gridOptions) {
        setTimeout(() => { this.gridOptions.api.sizeColumnsToFit(); }, 100);
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.resizeGrid();
    },
    searchList() {
      this.pageNo = 1;
      this.getDeliveryAdviceList();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getDeliveryAdviceList();
    },
    prevPage() { this.pageNo--; this.getDeliveryAdviceList(); },
    nextPage() { this.pageNo++; this.getDeliveryAdviceList(); },

    resetForm() {
      this.allRowList = [];
      this.selectedRows = [];
      this.filters = { is_sub_hire: false };
      this.subHireValue = true;
      this.pageNo = 1;
      this.totalItems = 0;
      if (this.value) {
        this.getDeliveryAdviceList();
      }
    },

    getDeliveryAdviceList() {
      this.$bus.$emit("showLoader", true);
      const { is_sub_hire, ...otherFilters } = this.filters;
      const params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        statuses: "Created,Active,Scheduled",
        ...otherFilters,
        ...(is_sub_hire !== null && is_sub_hire !== undefined
          ? { is_sub_hire }
          : {}),
      };
      this.$api.deliveryAdvice
        .getDaListForSubhire(params)
        .then((res) => {
          this.allRowList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    submitForm() {
      this.confirmDialog = false;
      if (!this.selectedRows.length) return;
      this.$bus.$emit("showLoader", true);
      const payload = {
        da_ids: this.selectedRows.map((da) => da.id),
        is_sub_hire: this.subHireValue,
      };
      this.$api.deliveryAdvice
        .bulkUpdateSubhire(payload)
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: `${res.data.queued} DA(s) queued for sub-hire update!`,
            color: "success",
          });
          this.selectedRows = [];
          this.getDeliveryAdviceList();
          this.$emit("refreshList");
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Failed to update sub-hire flag!",
            color: "error",
          });
          console.error(err);
        });
    },
  },
};
</script>
